import {isString} from 'lodash';
import React, {Children, ReactNode} from 'react';

import {isValidMention, transformMentions} from '../../../../src/helpers/models/commentHelpers';
import {DecoratedComponent} from '../markdownDecoratedComponents';
import {NodeTransform} from '../markdownFormatterTypes';

export interface MentionComponentMap {
  UserMentionWrapper: DecoratedComponent<{children: ReactNode; color: string | undefined}>;
  NormalMentionWrapper: DecoratedComponent<{children: ReactNode; color: string | undefined}>;
}

interface TransformProps {
  mentionableAliases: ReadonlySet<string>;
  userAlias: string | undefined;
  color: string | undefined;
  components: MentionComponentMap;
}

/**
 * Cross platform transform that will wrap strings matching teammates handles (mentions) in specific components (for styling).
 * @returns the function that transforms react nodes into react nodes with wrapped handles
 */
export function commonMakeMarkdownTransformMentions(transformProps: TransformProps): NodeTransform {
  const {mentionableAliases, userAlias, color, components} = transformProps;
  const {UserMentionWrapper, NormalMentionWrapper} = components;

  const userAliasSet = userAlias && new Set([userAlias]);

  return (node) =>
    Children.map(node, (child) => {
      // Only process strings.
      if (!isString(child)) {
        return child;
      }

      // Replace the mentions with our custom component.
      return transformMentions(child, (key, mention) => {
        const shouldIgnoreEmails = userAlias === mention;

        // Special appearance for a mention of our own user.
        if (userAliasSet && isValidMention(userAliasSet, mention, shouldIgnoreEmails)) {
          return (
            <UserMentionWrapper key={key} color={color}>
              {mention}
            </UserMentionWrapper>
          );
        }

        // Default appearance for any other mention.
        if (isValidMention(mentionableAliases, mention)) {
          return (
            <NormalMentionWrapper key={key} color={color}>
              {mention}
            </NormalMentionWrapper>
          );
        }

        return mention;
      });
    });
}
